import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import styled from '@emotion/styled';
import { Target, Route, Snowflake } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import ModalWrapper, { ModalCard } from '../ModalWrapper';
import PivotPointBranchView from './PivotPointBranchView';
import PivotPointBranchViewTree from './PivotPointBranchViewTree';
import PivotPointBranchViewCardinal from './PivotPointBranchViewCardinal';

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: white;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ModalTabs = styled.div`
  display: flex;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const ModalTab = styled.button`
  flex: 1;
  padding: 14px 16px;
  background: ${props => props.$active ? 'rgba(156, 39, 176, 0.15)' : 'transparent'};
  border: none;
  border-bottom: 3px solid ${props => props.$active ? '#9C27B0' : 'transparent'};
  color: ${props => props.$active ? 'white' : 'rgba(255, 255, 255, 0.5)'};
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  &:hover { background: rgba(255, 255, 255, 0.05); color: white; }
`;

const InfoSection = styled.div`
  margin-bottom: 20px;
`;

const InfoLabel = styled.div`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  margin-bottom: 6px;
`;

const InfoText = styled.div`
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.5;
`;

const MemberNode = styled.div`
  padding: 12px 14px;
  margin-bottom: 8px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  border-left: 4px solid ${props => props.$color || '#9C27B0'};
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover {
    background: ${props => `${props.$color || '#9C27B0'}22`};
    transform: translateX(4px);
  }
  &:last-child { margin-bottom: 0; }
`;

const MemberNodeText = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 8px;
  line-height: 1.4;
`;

const MemberNodeMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.75rem;
`;

const MemberScenarioBadge = styled.span`
  padding: 3px 8px;
  border-radius: 10px;
  background: ${props => `${props.$color || '#9C27B0'}33`};
  color: ${props => props.$color || '#9C27B0'};
  font-weight: 500;
`;

const GraphContainer = styled.div`
  position: relative;
  height: clamp(320px, 60vh, 620px);
  width: 100%;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.25);
`;

const LAYOUT_CYCLE = ['radial', 'tree', 'cardinal'];

const describeArc = (cx, cy, r, startDeg, endDeg) => {
  const startRad = (startDeg - 90) * Math.PI / 180;
  const endRad = (endDeg - 90) * Math.PI / 180;
  const x1 = cx + r * Math.cos(startRad);
  const y1 = cy + r * Math.sin(startRad);
  const x2 = cx + r * Math.cos(endRad);
  const y2 = cy + r * Math.sin(endRad);
  const largeArc = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
};

/**
 * PivotPointDetailModal - Shared modal for pivot point details.
 * Used by both PivotPointsScreen (Day 1) and PivotPointCurationView (Day 2 Lenses).
 *
 * 3 tabs: Info, Paths, Map (snowflake / tree / cardinal).
 */
export default function PivotPointDetailModal({
  pivotPoint,
  nodes = [],
  scenarios = [],
  onClose,
  onMemberClick,
}) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('info');
  const [layoutMode, setLayoutMode] = useState('radial');
  const [graphSize, setGraphSize] = useState({ width: 0, height: 0 });
  const graphRef = useRef(null);

  const memberNodes = useMemo(() => (
    (pivotPoint?.memberIds || [])
      .map(id => nodes.find(n => n.id === id))
      .filter(Boolean)
  ), [pivotPoint, nodes]);

  const memberScenarioGroups = useMemo(() => {
    const groups = {};
    memberNodes.forEach(node => {
      const scenario = scenarios.find(s => s.id === node.scenarioId);
      const name = scenario?.title || t('pivotPoints.common.unknown');
      const color = scenario?.color || '#607d8b';
      if (!groups[name]) groups[name] = { color, nodes: [] };
      groups[name].nodes.push(node);
    });

    const entries = Object.entries(groups).sort((a, b) => b[1].nodes.length - a[1].nodes.length);
    const total = memberNodes.length;

    let startAngle = 0;
    const pieSlices = entries.map(([, data]) => {
      const angle = total ? (data.nodes.length / total) * 360 : 0;
      const slice = { count: data.nodes.length, color: data.color, startAngle, endAngle: startAngle + angle };
      startAngle += angle;
      return slice;
    });

    return { entries, total, pieSlices };
  }, [memberNodes, scenarios, t]);

  useEffect(() => {
    if (activeTab !== 'map') return undefined;
    const el = graphRef.current;
    if (!el) return undefined;
    const update = () => {
      const { width, height } = el.getBoundingClientRect();
      if (width > 0 && height > 0) {
        setGraphSize({ width: Math.round(width), height: Math.round(height) });
      }
    };
    update();
    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    return () => ro.disconnect();
  }, [activeTab, pivotPoint?.id]);

  const onToggleLayout = useCallback(() => {
    setLayoutMode(prev => {
      const idx = LAYOUT_CYCLE.indexOf(prev);
      return LAYOUT_CYCLE[(idx + 1) % LAYOUT_CYCLE.length];
    });
  }, []);

  if (!pivotPoint) return null;

  const title = pivotPoint.text || pivotPoint.title || 'Untitled';
  const description = pivotPoint.description || t('pivotPoints.detail.noDescription');

  const BranchComponent = layoutMode === 'tree'
    ? PivotPointBranchViewTree
    : layoutMode === 'cardinal'
      ? PivotPointBranchViewCardinal
      : PivotPointBranchView;

  return (
    <ModalWrapper visible onClose={onClose} zIndex={1200} opacity={0.8}>
      <ModalCard size="xxl" onClick={(e) => e.stopPropagation()} style={{ border: '1px solid rgba(156, 39, 176, 0.3)' }}>
        <ModalCard.Header>
          <ModalTitle>
            <Target size={22} style={{ color: '#9C27B0' }} />
            {title}
          </ModalTitle>
        </ModalCard.Header>

        <ModalTabs>
          <ModalTab $active={activeTab === 'info'} onClick={() => setActiveTab('info')}>
            <Target size={16} />
            {t('pivotPoints.detail.tabInfo')}
          </ModalTab>
          <ModalTab $active={activeTab === 'members'} onClick={() => setActiveTab('members')}>
            <Route size={16} />
            {t('pivotPoints.detail.tabPaths', { count: pivotPoint.memberIds?.length || 0 })}
          </ModalTab>
          {memberNodes.length > 0 && (
            <ModalTab $active={activeTab === 'map'} onClick={() => setActiveTab('map')}>
              <Snowflake size={16} />
              {t('pivotPoints.detail.tabMap')}
            </ModalTab>
          )}
        </ModalTabs>

        {activeTab === 'map' ? (
          <GraphContainer ref={graphRef}>
            {graphSize.width > 0 && graphSize.height > 0 && (
              <BranchComponent
                pivotPoint={pivotPoint}
                memberNodes={memberNodes}
                scenarios={scenarios}
                width={graphSize.width}
                height={graphSize.height}
                onInfoClick={() => setActiveTab('info')}
                onMemberClick={onMemberClick}
                layoutMode={layoutMode}
                onToggleLayout={onToggleLayout}
              />
            )}
          </GraphContainer>
        ) : (
          <ModalCard.Body>
            {activeTab === 'info' && (
              <>
                <InfoSection>
                  <InfoLabel>{t('pivotPoints.detail.description')}</InfoLabel>
                  <InfoText>{description}</InfoText>
                </InfoSection>

                {memberScenarioGroups.total > 0 && (
                  <InfoSection style={{ marginTop: '20px' }}>
                    <InfoLabel>{t('pivotPoints.detail.linkedPaths', { count: memberScenarioGroups.total })}</InfoLabel>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', marginTop: '8px' }}>
                      <svg width="50" height="50" viewBox="0 0 50 50" style={{ flexShrink: 0 }}>
                        {memberScenarioGroups.pieSlices.map((slice, i) => (
                          <path key={i} d={describeArc(25, 25, 23, slice.startAngle, slice.endAngle)} fill={slice.color} stroke="#1a1a2e" strokeWidth="1" />
                        ))}
                      </svg>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                        {memberScenarioGroups.entries.map(([name, data]) => (
                          <div key={name}>
                            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: data.color, marginBottom: '2px' }}>
                              {name} ({data.nodes.length})
                            </div>
                            <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.4 }}>
                              {data.nodes.slice(0, 3).map((node, i) => (
                                <span key={node.id}>
                                  {node.text?.substring(0, 40)}{node.text?.length > 40 ? '...' : ''}
                                  {i < Math.min(data.nodes.length, 3) - 1 ? ', ' : ''}
                                </span>
                              ))}
                              {data.nodes.length > 3 && <span style={{ color: 'rgba(255,255,255,0.5)' }}> {t('pivotPoints.common.more', { count: data.nodes.length - 3 })}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </InfoSection>
                )}

                {pivotPoint.themes?.length > 0 && (
                  <InfoSection style={{ marginTop: '20px' }}>
                    <InfoLabel>{t('pivotPoints.detail.themes')}</InfoLabel>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                      {pivotPoint.themes.map(theme => (
                        <span key={theme} style={{
                          padding: '4px 10px', borderRadius: '12px',
                          background: 'rgba(255, 255, 255, 0.1)', color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.8rem',
                        }}>
                          {theme}
                        </span>
                      ))}
                    </div>
                  </InfoSection>
                )}
              </>
            )}

            {activeTab === 'members' && (
              <div>
                {memberNodes.map(memberNode => {
                  const memberScenario = scenarios.find(s => s.id === memberNode.scenarioId);
                  const color = memberScenario?.color || '#9C27B0';
                  return (
                    <MemberNode key={memberNode.id} $color={color} onClick={() => onMemberClick?.(memberNode)}>
                      <MemberNodeText>{memberNode.text}</MemberNodeText>
                      <MemberNodeMeta>
                        {memberScenario && (
                          <MemberScenarioBadge $color={color}>
                            {memberScenario.title}
                          </MemberScenarioBadge>
                        )}
                        {memberNode.participant && (
                          <span style={{ color: 'rgba(255,255,255,0.5)' }}>
                            {memberNode.participant}
                          </span>
                        )}
                      </MemberNodeMeta>
                    </MemberNode>
                  );
                })}
                {memberNodes.length === 0 && (
                  <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', padding: '20px' }}>
                    {t('pivotPoints.detail.noMembers')}
                  </div>
                )}
              </div>
            )}
          </ModalCard.Body>
        )}
      </ModalCard>
    </ModalWrapper>
  );
}
